// Dispatch → Today/Tonight: the mobile-first, day-of operational view. Shows a
// single day's tours as big cards bucketed LIVE / Upcoming / Done, with guest
// counts (PAX), capacity badges, and one-tap guide contact. Gaps surface the
// ✨-available guides to call plus a ready-to-send coverage SMS.
//
// Reuses the same Notion-fed PlanSlot data as the Plan board. Per-customer
// booking detail is deferred (would come from the fft-scheduling Worker's
// operator-API endpoints), and Peek guide swaps happen in the Peek UI — the
// Peek API is add-only.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertTriangle,
  Users,
  Phone,
  MessageSquare,
  Radio,
  Clock,
  Search,
} from "lucide-react";
import type { GuideRef, PlanSlot } from "@/lib/load";
import { fetchSlots } from "@/lib/load";
import { demoSlots } from "@/lib/demoData";
import { candidatesFor } from "@/lib/eligibility";
import { guideByPeekId, lookupGuideByAnyName } from "@/config/guides";
import { azTodayISO, plusDaysISO, slotStartMin } from "@/lib/dates";

const DURATION_MIN = 90;
const isDemo = () =>
  typeof window !== "undefined" && new URLSearchParams(window.location.search).has("demo");

const MARKET_STYLES: Record<string, string> = {
  FLG: "bg-emerald-100 text-emerald-700",
  PSCT: "bg-amber-100 text-amber-700",
  TUS: "bg-sky-100 text-sky-700",
};

type Phase = "live" | "upcoming" | "done";

interface Contact {
  name: string;
  phone?: string;
  email?: string;
}

function contactByName(name: string): Contact {
  const g = lookupGuideByAnyName(name);
  return g ? { name: g.peekName, phone: g.phone, email: g.email } : { name };
}
function contactByRef(ref: GuideRef): Contact {
  const g = guideByPeekId[ref.id];
  return { name: ref.peekName, phone: g?.phone, email: g?.email };
}

function phaseOf(slot: PlanSlot, now: Date): Phase {
  // Build the start from date + effective start time (label fallback) — raw
  // startIso is midnight for date-only Notion rows, which showed tonight's
  // tours as "Done" all day.
  const min = slotStartMin(slot);
  if (min == null) return "upcoming";
  const hh = String(Math.floor(min / 60)).padStart(2, "0");
  const mm = String(min % 60).padStart(2, "0");
  const start = new Date(`${slot.date}T${hh}:${mm}:00`);
  if (Number.isNaN(start.getTime())) return "upcoming";
  const end = new Date(start.getTime() + DURATION_MIN * 60000);
  if (now >= start && now < end) return "live";
  return start > now ? "upcoming" : "done";
}

function capBadge(slot: PlanSlot): { label: string; cls: string } | null {
  if (slot.capacity == null || slot.guests == null) return null;
  const left = slot.capacity - slot.guests;
  if (left <= 0) return { label: "FULL", cls: "bg-red-100 text-red-700" };
  const tight = Math.max(3, Math.round(slot.capacity * 0.15));
  if (left <= tight) return { label: `${left} left`, cls: "bg-amber-100 text-amber-700" };
  return { label: `${left} open`, cls: "bg-emerald-100 text-emerald-700" };
}

function coverageSMS(firstName: string, slot: PlanSlot): string {
  return `Hi ${firstName}, can you cover ${slot.tour} on ${formatDay(slot.date)} at ${slot.startLabel}? Please reply YES or NO THANKS. Thank you!`;
}

export default function DispatchToday() {
  const [date, setDate] = useState(azTodayISO());
  const [slots, setSlots] = useState<PlanSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [demo, setDemo] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isDemo()) {
        setSlots(demoSlots());
        setDemo(true);
      } else {
        setSlots(await fetchSlots(date, date));
        setDemo(false);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    void load();
  }, [load]);

  // Tick every 60s so LIVE/Upcoming/Done buckets track real time on a view
  // that's left open during operations (not frozen at load time).
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNowTick(Date.now()), 60_000);
    return () => window.clearInterval(t);
  }, []);

  const buckets = useMemo(() => {
    const now = new Date(nowTick);
    const b: Record<Phase, PlanSlot[]> = { live: [], upcoming: [], done: [] };
    for (const s of slots) b[phaseOf(s, now)].push(s);
    for (const k of Object.keys(b) as Phase[])
      b[k].sort(
        (a, c) => (slotStartMin(a) ?? 9999) - (slotStartMin(c) ?? 9999) || a.hhmm.localeCompare(c.hhmm),
      );
    return b;
  }, [slots, nowTick]);

  const step = (days: number) => setDate(plusDaysISO(date, days));

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      {/* Day nav */}
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3">
        <button
          onClick={() => step(-1)}
          className="rounded-md p-2 text-slate-500 hover:bg-slate-100"
          aria-label="Previous day"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="text-center">
          <div className="text-base font-semibold text-slate-800">{formatDay(date)}</div>
          <div className="mt-0.5 flex items-center justify-center gap-2">
            <input
              type="date"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              className="rounded border border-slate-200 px-1.5 py-0.5 text-xs text-slate-600"
              aria-label="Jump to date"
            />
            {date !== azTodayISO() && (
              <button onClick={() => setDate(azTodayISO())} className="text-xs font-medium text-sky-600 hover:underline">
                today
              </button>
            )}
          </div>
        </div>
        <button
          onClick={() => step(1)}
          className="rounded-md p-2 text-slate-500 hover:bg-slate-100"
          aria-label="Next day"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <Loader2 size={18} className="animate-spin" /> Loading…
        </div>
      )}
      {demo && !loading && (
        <div className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600">
          Demo data (?demo=1) — not live Notion.
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div className="font-mono text-xs">{error}</div>
        </div>
      )}

      {!loading &&
        (["live", "upcoming", "done"] as Phase[]).map((phase) =>
          buckets[phase].length ? (
            <section key={phase} className="space-y-2">
              <h2 className="flex items-center gap-1.5 px-1 text-xs font-bold uppercase tracking-wide text-slate-400">
                {phase === "live" && <Radio size={13} className="text-red-500" />}
                {phase === "upcoming" && <Clock size={13} />}
                {phase === "live" ? "Live now" : phase === "upcoming" ? "Upcoming" : "Done"}
                <span className="font-normal">({buckets[phase].length})</span>
              </h2>
              {buckets[phase].map((s) => (
                <TourCard key={s.pageId} slot={s} phase={phase} />
              ))}
            </section>
          ) : null,
        )}

      {!loading && slots.length === 0 && !error && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
          No tours scheduled for this day.
        </div>
      )}
    </div>
  );
}

function TourCard({ slot, phase }: { slot: PlanSlot; phase: Phase }) {
  const marketCls = slot.market ? MARKET_STYLES[slot.market] : "bg-slate-100 text-slate-500";
  const cap = capBadge(slot);
  // Names first; fall back to the Peek id (a Worker-synced assignment can carry
  // the cmau_ id with no People-prop name) so real assignments never read as gaps.
  const assigned = slot.assignedGuideNames.length
    ? slot.assignedGuideNames.map(contactByName)
    : slot.assignedGuidePeekId && guideByPeekId[slot.assignedGuidePeekId]
      ? [contactByName(guideByPeekId[slot.assignedGuidePeekId].peekName)]
      : [];
  const isGap = assigned.length === 0 && !slot.assignedGuidePeekId;
  return (
    <div
      className={
        "rounded-xl border bg-white p-4 " +
        (phase === "live" ? "border-red-300 ring-1 ring-red-200" : "border-slate-200") +
        (phase === "done" ? " opacity-60" : "")
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-slate-900">{slot.startLabel || slot.hhmm}</span>
            {phase === "live" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                <Radio size={10} /> Live
              </span>
            )}
          </div>
          <div className="mt-0.5">
            <span className={"mr-2 rounded px-1.5 py-0.5 text-xs font-semibold " + marketCls}>
              {slot.market ?? "?"}
            </span>
            <span className="font-medium text-slate-700">{slot.tour}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-1 text-lg font-bold text-slate-900">
            <Users size={16} className="text-slate-400" />
            {slot.guests != null ? slot.guests : "—"}
          </div>
          {cap && (
            <span className={"mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold " + cap.cls}>
              {cap.label}
            </span>
          )}
        </div>
      </div>

      {/* Guide row */}
      <div className="mt-3 border-t border-slate-100 pt-3">
        {isGap ? (
          <GapCoverage slot={slot} />
        ) : (
          <div className="space-y-2">
            <div className="space-y-1.5">
              {assigned.map((c) => (
                <div key={c.name} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">{c.name}</span>
                  <ContactButtons contact={c} />
                </div>
              ))}
            </div>
            <CoverageAlternates slot={slot} assignedNames={slot.assignedGuideNames} />
          </div>
        )}
      </div>
    </div>
  );
}

function GapCoverage({ slot }: { slot: PlanSlot }) {
  const { preferred, open } = candidatesFor(slot);
  const preferredContacts = preferred.map(contactByRef);
  const openContacts = open.map(contactByRef);
  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-red-600">
        <AlertTriangle size={14} /> {slot.excluded ? "No guide (excluded tour)" : "Needs a guide"}
      </div>
      {preferredContacts.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-slate-400">✨ available to call:</div>
          {preferredContacts.map((c) => (
            <div key={c.name} className="flex items-center justify-between">
              <span className="text-sm text-slate-700">{c.name}</span>
              <ContactButtons contact={c} sms={coverageSMS(c.name.split(" ")[0], slot)} />
            </div>
          ))}
        </div>
      )}
      {openContacts.length > 0 && (
        <div className={"space-y-1.5" + (preferredContacts.length ? " mt-2" : "")}>
          <div className="text-xs font-medium text-slate-400">no response — could ask:</div>
          {openContacts.map((c) => (
            <div key={c.name} className="flex items-center justify-between">
              <span className="text-sm text-slate-700">{c.name}</span>
              <ContactButtons contact={c} sms={coverageSMS(c.name.split(" ")[0], slot)} />
            </div>
          ))}
        </div>
      )}
      {preferredContacts.length === 0 && openContacts.length === 0 && (
        <div className="text-xs text-slate-400">
          {slot.excluded
            ? "Excluded from the picker."
            : "Nobody assignable — every eligible guide marked ❌."}
        </div>
      )}
    </div>
  );
}

// Coverage/capacity lookup: assignable guides for an already-assigned slot — to
// replace a guide who dropped, add a second guide near sell-out, or field a
// private request. ✨ guides listed first, then unmarked ("no response") guides;
// ❌ guides never appear.
function CoverageAlternates({ slot, assignedNames }: { slot: PlanSlot; assignedNames: string[] }) {
  const [open, setOpen] = useState(false);
  // Filter by guide ID, not display name — five guides have divergent
  // Peek/Notion identities and a raw-name compare re-offers the assigned guide
  // as their own coverage.
  const assignedIds = new Set(
    assignedNames.map((n) => lookupGuideByAnyName(n)?.id).filter(Boolean),
  );
  if (slot.assignedGuidePeekId) assignedIds.add(slot.assignedGuidePeekId);
  const cands = candidatesFor(slot);
  const preferredAlts = cands.preferred.filter((g) => !assignedIds.has(g.id)).map(contactByRef);
  const openAlts = cands.open.filter((g) => !assignedIds.has(g.id)).map(contactByRef);
  if (preferredAlts.length === 0 && openAlts.length === 0) return null;
  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs font-medium text-sky-600 hover:underline"
      >
        <Search size={12} /> Find coverage · {preferredAlts.length + openAlts.length} assignable
        {preferredAlts.length ? ` (${preferredAlts.length} ✨)` : ""}
      </button>
      {open && (
        <div className="mt-1.5 space-y-1.5 rounded-lg bg-slate-50 p-2">
          {preferredAlts.length > 0 && (
            <>
              <div className="text-[11px] font-medium text-slate-400">✨ available for this slot:</div>
              {preferredAlts.map((c) => (
                <div key={c.name} className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">{c.name}</span>
                  <ContactButtons contact={c} sms={coverageSMS(c.name.split(" ")[0], slot)} />
                </div>
              ))}
            </>
          )}
          {openAlts.length > 0 && (
            <>
              <div className="text-[11px] font-medium text-slate-400">no response — could ask:</div>
              {openAlts.map((c) => (
                <div key={c.name} className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">{c.name}</span>
                  <ContactButtons contact={c} sms={coverageSMS(c.name.split(" ")[0], slot)} />
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ContactButtons({ contact, sms }: { contact: Contact; sms?: string }) {
  if (!contact.phone) return <span className="text-xs text-slate-300">no number</span>;
  const tel = contact.phone.replace(/[^\d+]/g, "");
  const smsHref = `sms:${tel}${sms ? `?&body=${encodeURIComponent(sms)}` : ""}`;
  return (
    <div className="flex items-center gap-1">
      <a
        href={`tel:${tel}`}
        className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
      >
        <Phone size={12} /> Call
      </a>
      <a
        href={smsHref}
        className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
      >
        <MessageSquare size={12} /> Text
      </a>
      {sms && (
        <button
          onClick={() => void navigator.clipboard?.writeText(sms)}
          title="Copy coverage SMS"
          className="inline-flex items-center rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
        >
          Copy
        </button>
      )}
    </div>
  );
}

function formatDay(ymd: string): string {
  const d = new Date(`${ymd}T12:00:00`);
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}
